import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import { desativarSSE } from './services/EventSourceCleanup';

// Contexts
import { ThemeProvider } from './contexts/ThemeContext';
import MuiThemeBridge from './theme/MuiThemeBridge';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { KanbanProvider } from './contexts/KanbanContext';
import { AtividadeProvider } from './contexts/AtividadeContext';
import { ModalProvider } from './contexts/ModalContext';
import { DepartamentoProvider } from './contexts/DepartamentoContext';
import { UsuarioProvider } from './contexts/UsuarioContext';
import { NavegacaoProvider } from './contexts/NavegacaoContext';
import { AvisoProvider } from './contexts/AvisoContext';
import { NotificacaoProvider } from './contexts/NotificacaoContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { KanbanAdminProvider } from './contexts/KanbanAdminContext';
import { ComunicacaoProvider } from './contexts/ComunicacaoContext';
// Tutorial removido
// KanbanTutorialProvider removido

// Components
import Login from './components/auth/Login';
import Layout from './components/layout/Layout';
import AlterarSenhaObrigatoriaModal from './components/profile/AlterarSenhaObrigatoriaModal';
import AlterarSenhaGmailModal from './components/profile/AlterarSenhaGmailModal';
import Dashboard from './components/dashboard/Dashboard';
import KanbanBoard from './components/kanban/KanbanBoard';
import Profile from './components/profile/Profile';
import Settings from './components/profile/Settings';
import BuscaTarefasPage from './pages/BuscaTarefasPage';
import GerenciamentoUsuarios from './components/admin/GerenciamentoUsuarios';
import Avisos from './pages/Avisos';
import ComunicacaoPage from './pages/ComunicacaoPage';
import TarefaRedirecionador from './components/tarefas/TarefaRedirecionador';
import AtividadesDepartamentoPage from './pages/AtividadesDepartamentoPage';
import TarefasAtivasDepartamentoPage from './pages/TarefasAtivasDepartamentoPage';
import KanbanAdminPage from './pages/KanbanAdminPage';
import RelatoriosPage from './pages/RelatoriosPage';
import RelatorioTemporizadorPage from './pages/RelatorioTemporizadorPage';
import BuscaTarefasAdmin from './components/admin/BuscaTarefasAdmin';
import DocumentosIAPage from './pages/DocumentosIAPage';
import BravoAIPage from './pages/BravoAIPage';
import TarefasArquivadasPage from './pages/TarefasArquivadasPage';
import ListaSalas from './components/reservas/ListaSalas';
import CalendarioReservas from './components/reservas/CalendarioReservas';
import FormularioReserva from './components/reservas/FormularioReserva';
import MinhasReservas from './components/reservas/MinhasReservas';
import ChatPage from './pages/ChatPage';
import MonitorChatPage from './pages/MonitorChatPage';
import FormulariosAdminPage from './pages/FormulariosAdminPage';

import ModalDetalhesProvider from './components/kanban/ModalDetalhesProvider';
import ErrorBoundary from './components/common/ErrorBoundary';
import Notificacoes from './components/notificacoes/Notificacoes';
import TarefasPendentesPopup from './components/notifications/TarefasPendentesPopup';
import notificacaoNativaService from './services/NotificacaoNativaService';

// Componente para exibir modal de alteração de senha obrigatória
const ModalAlteracaoSenhaObrigatoria: React.FC = () => {
  const { requerAlteracaoSenha, isAuthenticated } = useAuth();
  
  // Só exibir se o usuário estiver autenticado e precisar alterar senha
  if (!isAuthenticated || !requerAlteracaoSenha) {
    return null;
  }
  
  return <AlterarSenhaObrigatoriaModal />;
};

// Componente para exibir modal de senha do Gmail
const ModalSenhaGmail: React.FC = () => {
  const { requerSenhaGmail, isAuthenticated, requerAlteracaoSenha } = useAuth();
  
  // Só exibir se:
  // 1. Usuário estiver autenticado
  // 2. Precisar configurar senha do Gmail
  // 3. Modal de alteração de senha não estiver aberto
  if (!isAuthenticated || !requerSenhaGmail || requerAlteracaoSenha) {
    return null;
  }
  
  return <AlterarSenhaGmailModal />;
};

// Componente para proteger rotas
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { usuario, verificandoAutenticacao } = useAuth();
  const location = useLocation();

  // Enquanto o token salvo ainda está sendo validado, 'usuario' é null mas
  // isso não quer dizer "deslogado" — sem essa checagem, TODO carregamento
  // de página (inclusive depois de uma ação que atualiza dados) piscava a
  // tela de login antes de voltar pra tela certa.
  if (verificandoAutenticacao) {
    return null;
  }

  if (!usuario) {
    // Preserva pra onde o usuário estava tentando ir (ex: link de email com
    // ?tarefaId=/?canalId=/&topicoId=) pra retomar depois do login, em vez
    // de sempre cair na Home.
    const destino = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${destino}`} replace />;
  }

  return <>{children}</>;
};

// Redireciona hashes (#/rota) para o BrowserRouter
const HashRedirect: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#/')) {
      const path = hash.substring(1); // remove '#'
      navigate(path, { replace: true });
    }
  }, [navigate]);

  return null;
};

// Processa ?redirect=/rota para manter navegação após login/refresh.
// Preserva QUALQUER outro parâmetro presente (tarefaId, canalId, topicoId,
// avisoId, etc.) na navegação, para que os links de deep-link vindos de
// email funcionem independente do destino (card, tópico, aviso...).
const RedirectParamHandler: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');

    if (redirect) {
      const decoded = decodeURIComponent(redirect);

      // Preserva todos os outros parâmetros, exceto o próprio 'redirect'
      const restante = new URLSearchParams(params);
      restante.delete('redirect');
      const query = restante.toString();

      navigate(query ? `${decoded}?${query}` : decoded, { replace: true });

      // remove parâmetro redirect da URL (mas mantém os demais, se existirem)
      const url = new URL(window.location.href);
      url.searchParams.delete('redirect');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  }, [navigate]);

  return null;
};

function App() {
  // Desativar qualquer conexão SSE existente ao iniciar o aplicativo
  useEffect(() => {
    desativarSSE();
    console.log('Conexões SSE desativadas ao iniciar o aplicativo');
    
    // Inicializar serviço de notificações nativas
    console.log('Inicializando serviço de notificações nativas...');
    notificacaoNativaService.solicitarPermissao().then(permissao => {
      if (permissao) {
        console.log('✅ Permissão para notificações nativas concedida!');
      } else {
        console.log('❌ Permissão para notificações nativas negada ou não suportada');
      }
    });
  }, []);

  return (
    <ThemeProvider>
    <MuiThemeBridge>
    <Router>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <AuthProvider>
        <ModalAlteracaoSenhaObrigatoria />
        <ModalSenhaGmail />
        {/* Tutorial removido */}
        {/* KanbanTutorialProvider removido */}
        <NotificacaoProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          
          <Route path="/" element={
            <ProtectedRoute>
              <KanbanProvider>
                <DepartamentoProvider>
                  <UsuarioProvider>
                  <ModalProvider>
                    <AtividadeProvider>
                      <AvisoProvider>
                        <ComunicacaoProvider>
                        <NavegacaoProvider>
                          <SidebarProvider>
                            <RedirectParamHandler />
                            <HashRedirect />
                            <ModalDetalhesProvider />
                            <ErrorBoundary>
                              <Layout />
                            </ErrorBoundary>
                          </SidebarProvider>
                        </NavegacaoProvider>
                        </ComunicacaoProvider>
                      </AvisoProvider>
                      </AtividadeProvider>
                    </ModalProvider>
                  </UsuarioProvider>
                </DepartamentoProvider>
              </KanbanProvider>
            </ProtectedRoute>
          }>
                    <Route index element={<Dashboard />} />
                    <Route path="kanban">
                      <Route index element={<Navigate to="/kanban/recebidas" replace />} />
                      <Route path="recebidas" element={<KanbanBoard tipo="recebidas" />} />
                      <Route path="enviadas" element={<KanbanBoard tipo="enviadas" />} />
                    </Route>
                    <Route path="perfil" element={<Profile />} />
                    <Route path="configuracoes" element={<Settings />} />
                    <Route path="busca-tarefas" element={<BuscaTarefasPage />} />
                    <Route path="admin/usuarios" element={<GerenciamentoUsuarios />} />
                    <Route path="admin/kanban-global" element={<KanbanAdminPage />} />
                    <Route path="admin/busca-tarefas" element={<BuscaTarefasAdmin />} />
                    <Route path="admin/documentos-ia" element={<DocumentosIAPage />} />
                    <Route path="bravo-ai" element={<BravoAIPage />} />
                    <Route path="admin/bravo-ai" element={<Navigate to="/bravo-ai" replace />} />
                    <Route path="lider/atividades" element={<AtividadesDepartamentoPage />} />
                    <Route path="lider/tarefas-ativas-departamento" element={<TarefasAtivasDepartamentoPage />} />
                    <Route path="avisos" element={<Avisos />} />
                    <Route path="comunicacao" element={<ComunicacaoPage />} />
                    <Route path="tarefas-arquivadas" element={<TarefasArquivadasPage />} />
                    <Route path="relatorios" element={<RelatoriosPage />} />
                    <Route path="relatorios/temporizador" element={<RelatorioTemporizadorPage />} />
                    <Route path="chat" element={<ChatPage />} />
                    <Route path="admin/monitor-chat" element={<MonitorChatPage />} />
                    <Route path="admin/formularios" element={<FormulariosAdminPage />} />
                    <Route path="reservas" element={<ListaSalas />} />
                    <Route path="reservas/minhas" element={<MinhasReservas />} />
                    <Route path="reservas/calendario/:salaId" element={<CalendarioReservas />} />
                    <Route path="reservas/nova/:salaId" element={<FormularioReserva />} />
                    <Route path="tarefas/:id" element={<TarefaRedirecionador />} />

                    {/* Redireciona para o dashboard se a rota não existir */}
                    <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
          <Notificacoes />
          {/* Popup de tarefas pendentes - aparece em todas as páginas quando necessário */}
          <TarefasPendentesPopup />
          {/* Tutorial de primeiro acesso removido */}
          {/* Tutorial específico para o Kanban removido */}
        </NotificacaoProvider>
        {/* Fim do KanbanTutorialProvider removido */}
        {/* Fim do Tutorial removido */}
      </AuthProvider>
    </Router>
    </MuiThemeBridge>
    </ThemeProvider>
  );
}

export default App;
